import { forwardRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

import { customPaymentMethods } from "@jti/fe-common";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectOrderNumber } from "@jti/store/selectors/selectOrderNumber";
import { getPgsAdapter, getPGSToken } from "@jti/store/slices/LEGACY__checkoutSlice";
import { deletePaymentTokenFromStorage } from "@jti/store/utils";

import { PaymentMethodSelector } from "@checkout/components/PaymentMethodSelector";
import usePgsClient from "@checkout/hooks/payment/usePgsClient";
import useDispatchGetPaymentMethods from "@checkout/hooks/useDispatchGetPaymentMethods/useDispatchGetPaymentMethods";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import usePaymentMethods from "@checkout/hooks/usePaymentMethods";
import { useSubscription } from "@checkout/hooks/useSubscription";

type PaymentPagePaymentMethodsContainerProps = {
  paymentMethodCode: string;
  handlePaymentMethodChange: (p: string) => void;
};

export const PaymentPagePaymentMethodsContainer = forwardRef<
  HTMLDivElement | null,
  PaymentPagePaymentMethodsContainerProps
>(({ paymentMethodCode, handlePaymentMethodChange }, ref) => {
  const { orderId } = useParams<{ orderId: string }>();
  const dispatch = useDispatch();
  const { isSubscription } = useSubscription();
  const { paymentMethods, hasOrderError, pgsNewOrderPaymentToken, orderPending } = usePaymentMethods();
  const { isFreeTrial } = useFreeTrial();
  const orderEntityId = useSelector(selectOrderNumber(isFreeTrial));

  const shouldFetchPgsPaymentToken = orderEntityId && !pgsNewOrderPaymentToken && !orderPending;

  useEffect(() => {
    if (shouldFetchPgsPaymentToken) {
      dispatch(getPGSToken({ orderEntityId, isFreeTrial, isSubscription }));
    }
  }, [orderEntityId, dispatch, getPGSToken, shouldFetchPgsPaymentToken]);

  useDispatchGetPaymentMethods();

  // TODO DESP-82430: remove using this hook here. It is used in PaymentPageFooter directly
  usePgsClient({
    ref,
    paymentCode: paymentMethodCode,
    onUnmount: deletePaymentTokenFromStorage,
    buttonClassName: "payButton",
    orderEntityId,
    orderId,
  });

  const handleSelect = useCallback(
    async (code: string) => {
      if (code) {
        handlePaymentMethodChange(code);

        if (code !== customPaymentMethods.payOnDelivery) {
          dispatch(getPgsAdapter({ code }));
        }
      }
    },
    [dispatch]
  );

  return hasOrderError ? null : <PaymentMethodSelector methods={paymentMethods} onSelect={handleSelect} />;
});
